/**
 * ProductDetailScreen - Alle info over een product
 */

import React, { useState, useEffect, useRef } from 'react';
import { View, Text, StyleSheet, ScrollView, TouchableOpacity, Animated, Image, Platform } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useTranslation } from 'react-i18next';
import type { NativeStackScreenProps } from '@react-navigation/native-stack';
import type { AllStackParamList } from '../models/Navigation';
import type { ProductDisplay, Grade } from '../models';
import { productService, favoritesService } from '../services';
import { WaveHeader, GradeDisplay, SustainabilityFactorCard, LoadingSpinner, ErrorDisplay } from '../components';

type Props = NativeStackScreenProps<AllStackParamList, 'ProductDetail'>;

export const ProductDetailScreen: React.FC<Props> = ({ navigation, route }) => {
  const { t, i18n } = useTranslation();
  const { productId } = route.params;

  const [product, setProduct] = useState<ProductDisplay | null>(null);
  const [aanHetLaden, setAanHetLaden] = useState(true);
  const [foutmelding, setFoutmelding] = useState<string | null>(null);
  const [isFavoriet, setIsFavoriet] = useState(false);
  const [imageError, setImageError] = useState(false);

  const hartjeSchaal = useRef(new Animated.Value(1)).current;

  // Product data inladen en kijken of we hem leuk vinden (favoriet)
  useEffect(() => {
    laadProduct();
    controleerFavorietStatus();
    setImageError(false);
  }, [productId]);

  // Alles ophalen wat we weten over dit ding
  const laadProduct = async () => {
    try {
      setAanHetLaden(true);
      setFoutmelding(null);
      const productData = await productService.getProductById(productId);

      if (!productData) {
        setFoutmelding(t('errors.productNotFound'));
      } else {
        setProduct(productData);
      }
    } catch (err) {
      console.error('Error loading product:', err);
      setFoutmelding(t('errors.databaseError'));
    } finally {
      setAanHetLaden(false);
    }
  };

  const controleerFavorietStatus = async () => {
    try {
      const favorietStatus = await favoritesService.isFavorite(
        productId.toString(),
      );
      setIsFavoriet(favorietStatus);
    } catch (err) {
      console.error('Error checking favorite status:', err);
    }
  };

  // Hartje aan of uit
  const wisselFavoriet = async () => {
    try {
      // Hartje animeren (boem boem)
      Animated.sequence([
        Animated.timing(hartjeSchaal, {
          toValue: 1.3,
          duration: 150,
          useNativeDriver: true,
        }),
        Animated.timing(hartjeSchaal, {
          toValue: 1,
          duration: 150,
          useNativeDriver: true,
        }),
      ]).start();

      if (isFavoriet) {
        await favoritesService.removeFavorite(productId.toString());
        setIsFavoriet(false);
      } else {
        await favoritesService.addFavorite(productId.toString());
        setIsFavoriet(true);
      }
    } catch (err) {
      console.error('Error toggling favorite:', err);
    }
  };

  // Moeten we alternatieven laten zien?
  // Alleen als het product slecht is voor de planeet (C of lager).
  // Als het al een A+ is, hoeven we niks beters te zoeken he.
  const moetAlternatievenTonen = (grade: Grade): boolean => {
    // Alleen alternatieven laten zien als het product prut is (C of lager)
    const lageCijfers = ['C+', 'C', 'C-', 'D+', 'D', 'D-', 'E+', 'E', 'E-'];
    return lageCijfers.includes(grade);
  };

  const getPhStatus = (ph: number) => {
    if (ph < 6.0 || ph > 9.0) return { style: stijlen.metriekGevaar, label: 'Poor' };
    if (ph < 6.5 || ph > 8.5) return { style: stijlen.metriekWaarschuwing, label: 'Fair' };
    return { style: stijlen.metriekGoed, label: 'Good' };
  };

  const getTdsStatus = (tds: number) => {
    if (tds > 500) return { style: stijlen.metriekGevaar, label: 'High' };
    if (tds > 300) return { style: stijlen.metriekWaarschuwing, label: 'Medium' };
    return { style: stijlen.metriekGoed, label: 'Excellent' };
  };

  const getTurbStatus = (turb: number) => {
    if (turb > 20) return { style: stijlen.metriekGevaar, label: 'Cloudy' };
    if (turb > 5) return { style: stijlen.metriekWaarschuwing, label: 'Fair' };
    return { style: stijlen.metriekGoed, label: 'Clear' };
  };

  const bekijkAlternatieven = () => {
    if (product) {
      navigation.navigate('Alternatives', {
        productId: product.id,
        currentGrade: product.grade,
      });
    }
  };

  // Even wachten... (laden)
  if (aanHetLaden) {
    return (
      <SafeAreaView style={stijlen.container} edges={['top']}>
        <LoadingSpinner message={t('common.loading')} />
      </SafeAreaView>
    );
  }

  // Oeps, er is iets stuk gegaan
  if (foutmelding || !product) {
    return (
      <SafeAreaView style={stijlen.container} edges={['top']}>
        <ErrorDisplay
          message={foutmelding || t('errors.productNotFound')}
          onRetry={laadProduct}
          showRetry={true}
        />
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={[stijlen.container, Platform.OS === 'web' && stijlen.webContainer]} edges={['top']}>
      {/* Header met logo en terug knop */}
      <View style={stijlen.kopNavigatie}>
        <WaveHeader showLogo={true} height={180} />

        {/* Terug knop */}
        <TouchableOpacity
          style={stijlen.terugKnop}
          onPress={() => navigation.goBack()}
          accessibilityLabel={t('accessibility.backButton')}
          accessibilityRole="button"
          accessibilityHint={t('common.back')}
          accessible={true}>
          <Text style={stijlen.terugKnopTekst} accessible={false}>←</Text>
        </TouchableOpacity>

        {/* Favorieten knop (het hartje) */}
        <Animated.View
          style={[
            stijlen.favorietKnop,
            { transform: [{ scale: hartjeSchaal }] },
          ]}>
          <TouchableOpacity
            onPress={wisselFavoriet}
            accessibilityLabel={
              isFavoriet
                ? t('accessibility.removeFromFavoritesButton')
                : t('accessibility.addToFavoritesButton')
            }
            accessibilityRole="button"
            accessibilityState={{ selected: isFavoriet }}
            accessible={true}>
            <Text style={stijlen.favorietIcoon} accessible={false}>
              {isFavoriet ? '❤️' : '🤍'}
            </Text>
          </TouchableOpacity>
        </Animated.View>
      </View>

      {/* Scrollen maar */}
      <ScrollView
        style={stijlen.scrollContainer}
        contentContainerStyle={stijlen.scrollInhoud}
        showsVerticalScrollIndicator={false}>

        {/* Plaatje van het product */}
        <View
          style={stijlen.afbeeldingBak}
          accessible={true}
          accessibilityLabel={t('accessibility.productImagePlaceholder')}>
          {product.imageUrl && !imageError ? (
            <Image
              source={{ uri: product.imageUrl }}
              style={stijlen.productAfbeelding}
              resizeMode="cover"
              onError={() => setImageError(true)}
            />
          ) : (
            <View style={stijlen.afbeeldingPlaatsvervanger}>
              <Text style={stijlen.afbeeldingPlaatsvervangerTekst} accessible={false}>📦</Text>
            </View>
          )}
        </View>

        {/* Naam van het product */}
        <Text
          style={stijlen.productNaam}
          maxFontSizeMultiplier={1.5}
          accessibilityRole="header">
          {product.name}
        </Text>

        {/* Merkje */}
        <Text
          style={stijlen.merkNaam}
          maxFontSizeMultiplier={1.5}>
          {product.brand}
        </Text>

        {/* Het cijfer */}
        <View style={stijlen.cijferBak}>
          <GradeDisplay
            grade={product.grade}
            size="large"
            showLabel={false}
            announceOnMount={true}
          />
          <Text
            style={stijlen.cijferLabel}
            maxFontSizeMultiplier={1.5}>
            {t('product.planetaryImpactScore')}
          </Text>
        </View>

        {/* Waarom het goed of slecht is */}
        <View style={stijlen.factorenBak}>
          <Text
            style={stijlen.sectieTitel}
            maxFontSizeMultiplier={1.5}
            accessibilityRole="header">
            {t('product.sustainabilityFactors')}
          </Text>

          {product.sustainabilityFactors.length > 0 ? (
            product.sustainabilityFactors.map((factor, index) => {
              const isEnglish = i18n.language.startsWith('en');
              const displayDescription = isEnglish && factor.description_en ? factor.description_en : factor.description;
              const displayFactor = { ...factor, description: displayDescription };
              return <SustainabilityFactorCard key={index} factor={displayFactor} />;
            })
          ) : (
            <View
              style={stijlen.geenFactorenBak}
              accessible={true}
              accessibilityRole="text">
              <Text
                style={stijlen.geenFactorenTekst}
                maxFontSizeMultiplier={1.5}
                accessible={false}>
                {(i18n.language.startsWith('en') && product.overallReason_en) ? product.overallReason_en : product.overallReason}
              </Text>
            </View>
          )}
        </View>

        {/* Water dingetjes */}
        {(product.phLevel !== undefined || product.tdsValue !== undefined) && (
          <View style={stijlen.metriekenBak}>
            <Text
              style={stijlen.sectieTitel}
              maxFontSizeMultiplier={1.5}
              accessibilityRole="header">
              {t('product.waterImpact', 'Water Impact Metrics')}
            </Text>

            <View style={stijlen.metriekenRooster}>
              {/* pH Waarde */}
              <View style={stijlen.metriekKaart}>
                <Text style={stijlen.metriekIcoon}>🧪</Text>
                <Text style={stijlen.metriekLabel}>pH Level</Text>
                <Text style={[
                  stijlen.metriekWaarde,
                  product.phLevel ? getPhStatus(product.phLevel).style : stijlen.metriekGoed
                ]}>
                  {product.phLevel}
                </Text>
                <Text style={stijlen.metriekSub}>
                  {product.phLevel ? getPhStatus(product.phLevel).label : '-'}
                </Text>
              </View>

              {/* TDS (Total Dissolved Solids) */}
              <View style={stijlen.metriekKaart}>
                <Text style={stijlen.metriekIcoon}>🧂</Text>
                <Text style={stijlen.metriekLabel}>TDS (ppm)</Text>
                <Text style={[
                  stijlen.metriekWaarde,
                  product.tdsValue ? getTdsStatus(product.tdsValue).style : stijlen.metriekGoed
                ]}>
                  {product.tdsValue}
                </Text>
                <Text style={stijlen.metriekSub}>
                  {product.tdsValue ? getTdsStatus(product.tdsValue).label : '-'}
                </Text>
              </View>

              {/* Turbidity */}
              <View style={stijlen.metriekKaart}>
                <Text style={stijlen.metriekIcoon}>🌫️</Text>
                <Text style={stijlen.metriekLabel}>Turbidity</Text>
                <Text style={[
                  stijlen.metriekWaarde,
                  product.turbidity ? getTurbStatus(product.turbidity).style : stijlen.metriekGoed
                ]}>
                  {product.turbidity}
                </Text>
                <Text style={stijlen.metriekSub}>
                  {product.turbidity ? getTurbStatus(product.turbidity).label : '-'}
                </Text>
              </View>
            </View>
          </View>
        )}

        {/* Knop voor betere opties */}
        {moetAlternatievenTonen(product.grade) && (
          <TouchableOpacity
            style={stijlen.alternatievenKnop}
            onPress={bekijkAlternatieven}
            accessibilityLabel={t('accessibility.viewAlternativesButton')}
            accessibilityRole="button"
            accessibilityHint={t('alternatives.description')}
            accessible={true}>
            <Text
              style={stijlen.alternatievenKnopTekst}
              maxFontSizeMultiplier={1.5}
              accessible={false}>
              {t('product.viewAlternatives')}
            </Text>
          </TouchableOpacity>
        )}

        {/* Wat ruimte onderin */}
        <View style={stijlen.bodemRuimte} />
      </ScrollView>
    </SafeAreaView>
  );
};

const stijlen = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F5F5F5',
  },
  webContainer: {
    maxWidth: 600,
    width: '100%',
    alignSelf: 'center',
    // @ts-ignore - Web schaduw dingetje (werkt niet op native)
    boxShadow: '0 0 20px rgba(0,0,0,0.1)',
    minHeight: Platform.OS === 'web' ? ('100vh' as any) : '100%',
  },
  kopNavigatie: {
    position: 'relative',
  },
  terugKnop: {
    position: 'absolute',
    top: 16,
    left: 16,
    minWidth: 44,
    minHeight: 44,
    width: 44,
    height: 44,
    borderRadius: 22,
    backgroundColor: 'rgba(255, 255, 255, 0.9)',
    justifyContent: 'center',
    alignItems: 'center',
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 2,
    },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
    zIndex: 10,
  },
  terugKnopTekst: {
    fontSize: 24,
    color: '#7B3FF2',
    fontWeight: 'bold',
  },
  favorietKnop: {
    position: 'absolute',
    top: 16,
    right: 16,
    minWidth: 44,
    minHeight: 44,
    width: 44,
    height: 44,
    borderRadius: 22,
    backgroundColor: 'rgba(255, 255, 255, 0.9)',
    justifyContent: 'center',
    alignItems: 'center',
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 2,
    },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
    zIndex: 10,
  },
  favorietIcoon: {
    fontSize: 24,
  },
  scrollContainer: {
    flex: 1,
  },
  scrollInhoud: {
    paddingHorizontal: 24,
    paddingTop: 16,
  },
  afbeeldingBak: {
    alignItems: 'center',
    marginBottom: 24,
  },
  afbeeldingPlaatsvervanger: {
    width: 120,
    height: 120,
    borderRadius: 12,
    backgroundColor: '#FFFFFF',
    justifyContent: 'center',
    alignItems: 'center',
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 2,
    },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  afbeeldingPlaatsvervangerTekst: {
    fontSize: 48,
  },
  productAfbeelding: {
    width: 200,
    height: 200,
    borderRadius: 20,
    backgroundColor: '#F1F5F9',
  },
  productNaam: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#333',
    textAlign: 'center',
    marginBottom: 8,
  },
  merkNaam: {
    fontSize: 16,
    color: '#666',
    textAlign: 'center',
    marginBottom: 24,
  },
  cijferBak: {
    alignItems: 'center',
    marginBottom: 32,
  },
  cijferLabel: {
    marginTop: 12,
    fontSize: 14,
    color: '#666',
    textAlign: 'center',
  },
  factorenBak: {
    marginBottom: 24,
  },
  sectieTitel: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#333',
    marginBottom: 16,
  },
  geenFactorenBak: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    padding: 16,
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 1,
    },
    shadowOpacity: 0.1,
    shadowRadius: 2,
    elevation: 2,
  },
  geenFactorenTekst: {
    fontSize: 14,
    color: '#666',
    lineHeight: 20,
  },
  alternatievenKnop: {
    backgroundColor: '#4A90E2',
    borderRadius: 24,
    paddingVertical: 16,
    paddingHorizontal: 24,
    alignItems: 'center',
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 2,
    },
    shadowOpacity: 0.15,
    shadowRadius: 4,
    elevation: 4,
    marginBottom: 16,
  },
  alternatievenKnopTekst: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#FFFFFF',
  },
  bodemRuimte: {
    height: 32,
  },
  metriekenBak: {
    marginBottom: 24,
  },
  metriekenRooster: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    gap: 12,
  },
  metriekKaart: {
    flex: 1,
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    padding: 12,
    alignItems: 'center',
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 1,
    },
    shadowOpacity: 0.1,
    shadowRadius: 2,
    elevation: 2,
  },
  metriekIcoon: {
    fontSize: 24,
    marginBottom: 8,
  },
  metriekLabel: {
    fontSize: 12,
    color: '#666',
    marginBottom: 4,
    textAlign: 'center',
  },
  metriekWaarde: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#333',
    marginBottom: 2,
  },
  metriekSub: {
    fontSize: 10,
    color: '#999',
    fontWeight: '600',
  },
  metriekGoed: {
    color: '#22C55E',
  },
  metriekGevaar: {
    color: '#EF4444',
  },
  metriekWaarschuwing: {
    color: '#F59E0B',
  },
});
